"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCcw, Home } from "lucide-react";
import Link from "next/link";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to Sentry manually since Next.js catches it here
    Sentry.captureException(error);
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-luxury p-12 text-center space-y-8 border border-slate-100">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto">
          <AlertCircle className="w-10 h-10 text-red-500" />
        </div>
        
        <div className="space-y-2">
          <h1 className="text-3xl font-headline font-black text-slate-900 tracking-tight">
            Something went wrong
          </h1>
          <p className="text-slate-500 font-medium leading-relaxed">
            An unexpected error occurred. Our team has been notified and we're looking into it.
          </p>
        </div>

        {error.digest && (
          <div className="py-2 px-4 bg-slate-50 rounded-xl border border-slate-100">
            <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">
              Error ID: {error.digest}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button 
            onClick={() => reset()}
            className="w-full h-12 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-bold gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <RefreshCcw className="w-4 h-4" />
            Try Again
          </Button>
          
          <Link href="/" className="w-full">
            <Button 
              variant="outline"
              className="w-full h-12 rounded-2xl border-slate-200 hover:bg-slate-50 text-slate-600 font-bold gap-2"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <p className="text-xs text-slate-400">
          If this persists, please contact support.
        </p>
      </div>
    </div>
  );
}
